import type { IconButtonProps } from './IconButton';
import type { ButtonAppearanceProps, ButtonSize } from 'components/Button/types';

/**
 * Maps the legacy MUI `IconButton` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary, exactly like `Buttons/Button/toDsProps`. It exists so the visual refresh
 * is gated inside the wrapper rather than at ~76 call sites; retiring the flag deletes it.
 *
 * Returning `null` means "DS 2.0 cannot express this button" and sends the caller back to MUI. A
 * silent best-effort mapping would ship buttons that are the wrong colour or the wrong size, and
 * nothing catches that at review time. Every `null` here is a design question.
 *
 * The accessible name is carried out in the result rather than re-read by the caller: this mapper is
 * what proves the label is present, so it should also be what hands it over already narrowed to
 * `string`. That keeps a non-null assertion out of the wrapper.
 */
type DsProps = ButtonAppearanceProps & { size: ButtonSize; 'aria-label': string; loading?: boolean };

/**
 * MUI icon-button sizes are 12 / 20-24 / 32 / 40 / 48; DS sizes are 32 / 36 / 40 / 44. Only two
 * cells line up exactly, and only exact matches are mapped - snapping `small` up to 32 would grow
 * the button by 8px and silently reflow whatever sits beside it.
 *
 * Unmapped, and why:
 *   - `small` (20-24) and `xs` (12) - DS has nothing below 32. `small` is the most common size in
 *     the codebase, so this is the single biggest reason a call site stays on MUI.
 *   - `action` (48) - DS stops at 44.
 */
const SIZE: Partial<Record<NonNullable<IconButtonProps['size']>, ButtonSize>> = {
  medium: 'xs',
  large: 'regular',
};

/**
 * Cases that stay on MUI:
 *
 *   - `sx` - an Emotion prop with no effect on a Tailwind component. Accepting it would drop the
 *     styles silently rather than fail.
 *   - no `aria-label` - the DS component requires an accessible name, and an icon button without
 *     one is unusable with a screen reader. `IconButtonWithTooltip` supplies it from `title`, so
 *     this only bites on bare `IconButton` call sites (which are already an a11y bug on MUI).
 *   - `variant` omitted - with no variant, `IconButton.styled` applies no block at all and MUI's own
 *     `color` prop is never forwarded, so the button renders as a bare transparent MUI default.
 *     There is no DS cell for "no styling".
 *   - `variant="plain"` - transparent with NO hover background. `muted / ghost` is the nearest fill
 *     but it does have a hover background, so this would change the interaction feel.
 *   - coloured `variant="text"` - `muted / ghost` draws its icon in `Color/Dark/1` (near-black), so
 *     mapping a deliberately blue or red text button onto it would silently discard the colour.
 *     Note `Buttons/Button/toDsProps` maps ALL text buttons to `muted / ghost` regardless of colour;
 *     that is worth revisiting there for the same reason.
 *   - `color="info"` - DS 2.0 has no informational appearance.
 */
export const toDsIconProps = ({
  color = 'primary',
  isLoading,
  size = 'medium',
  sx,
  variant,
  'aria-label': ariaLabel,
}: IconButtonProps): DsProps | null => {
  if (sx || !ariaLabel) {
    return null;
  }

  const dsSize = SIZE[size];

  if (!dsSize) {
    return null;
  }

  const base = { size: dsSize, 'aria-label': ariaLabel, loading: isLoading };

  // Early returns rather than one built object: `ButtonAppearanceProps` is a discriminated union, so
  // a flat `{ appearance, fill }` assembled from variables satisfies neither member.
  if (variant === 'contained') {
    if (color === 'primary') return { appearance: 'primary', fill: 'default', ...base };
    if (color === 'error') return { appearance: 'destructive', fill: 'default', ...base };
    // grey[100] background with a grey[700] icon, which is what `muted / default` draws
    // (`Color/Light/1` on `Color/Dark/6`).
    if (color === 'secondary') return { appearance: 'muted', fill: 'default', ...base };

    return null;
  }

  if (variant === 'outlined') {
    if (color === 'primary') return { appearance: 'primary', fill: 'outline', ...base };
    if (color === 'error') return { appearance: 'destructive', fill: 'outline', ...base };
    /**
     * `light` is what `ButtonMenu` defaults to for every non-text variant. Through the colour
     * mappers it resolves to a white background, a `grey[300]` border and a `grey[700]` icon - the
     * same neutral outline `muted / outline` draws, and the same recipe `toDsProps` already maps
     * `variant="outlined-grey"` onto for text buttons.
     *
     * `secondary` and `text` are deliberately absent: both fall through to `secondary.contrastText`
     * for the BORDER, i.e. a dark grey[700] outline rather than a light one. DS has no dark-outline
     * neutral cell, so they stay on MUI.
     */
    if (color === 'light') return { appearance: 'muted', fill: 'outline', ...base };

    return null;
  }

  if (variant === 'text' && color === 'text') {
    return { appearance: 'muted', fill: 'ghost', ...base };
  }

  return null;
};
